<script lang="ts">import { goto } from "$app/navigation";
import { Button } from "@infinitro/button";
import { Card } from "@infinitro/card";
import { toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { cfgCdnBase, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { loggedIn, providerBasePath, toCamelCase, toSiteUrl } from "@infinitro/global";
import { LocalIcon, RemoteIcon } from "@infinitro/icons";
import { Card as PriceCard } from "@infinitro/price-card";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import Swiper from "swiper";
import { Autoplay, FreeMode, Mousewheel, Navigation } from "swiper/modules";
import "swiper/swiper-bundle.css";
import { asGameInfo, asPriceItem, asRecord } from "./types";
// Exposed props for customization of the component
let { comingSoon = false, data, url, viewAllOnClick = null, title, icon = null, irregular = true, padding = "", CustomCard = null, cardWidth = "", cardHeight = "", spaceBetween = 8, slidesPerView = null, customSlidesPerView = {}, noViewAllLink = false, noCardLink = false, minHeight = false, customStyle = "", customStyleSwiperContainer = "", centerSlides = false, centerIndex = 0, titleNotClickable = false, centerIndexReady = false, callFromHomePage = false, touchRatio = 1, freeModeMomentumRatio = .5, freeModeMomentumVelocityRatio = .5, titleExtra } = $props();
$effect(() => {
	void minHeight;
});
// Define CDN base for asset URL generation
const CDN_BASE = cfgCdnBase();
// Calculate initial visible slides count based on container width
let visibleSlidesCount = $state(0);
// Sanitizing the title for use in dynamic IDs and other elements
let sanitizedTitle = $derived(title?.replace(/[^a-zA-Z0-9]/g, "-"));
let nextButtonId = $derived(`id-next-${sanitizedTitle}`);
let prevButtonId = $derived(`id-prev-${sanitizedTitle}`);
// Mounted flag to track component lifecycle
let mounted = $state(false);
// Declare swiper instance and related DOM elements
let swiper;
let swiperContainer = $state(undefined);
let containerRef = $state(undefined);
let swiperHeight = $state("107px");
let startX = 0;
let startY = 0;
// Prevent browser back gesture by detecting horizontal swipes
const handleTouchStart = (e) => {
	startX = e.touches[0].clientX;
	startY = e.touches[0].clientY;
};
const handleTouchMove = (e) => {
	const deltaX = e.touches[0].clientX - startX;
	const deltaY = e.touches[0].clientY - startY;
	// If horizontal swipe detected, prevent default to block browser back
	if (Math.abs(deltaX) > Math.abs(deltaY)) {
		e.preventDefault();
	}
};
const getSlidesPerView = () => {
	if (!swiperContainer) return 3;
	const containerWidth = swiperContainer.clientWidth;
	let slidesPerViewCount = 3;
	for (const breakpoint of Object.keys(customSlidesPerView).map(Number).sort((a, b) => a - b)) {
		if (containerWidth >= breakpoint) {
			slidesPerViewCount = customSlidesPerView[breakpoint];
		}
	}
	return slidesPerViewCount;
};
// Calculate the number of visible slides based on the container's width and swiper settings
const getVisibleSlidesCount = () => {
	if (!swiper || !swiperContainer) return 0;
	const slides = swiper.slides;
	const containerWidth = swiperContainer.clientWidth;
	let visibleWidth = 0;
	let visibleSlides = 0;
	if (!slides || slides.length === 0) return 0;
	// Calculate how many slides fit in the container based on the available space
	for (let i = swiper.activeIndex; i < slides.length; i++) {
		visibleWidth += slides[i].clientWidth + Number(swiper.params.spaceBetween);
		if (visibleWidth > containerWidth) break;
		visibleSlides++;
	}
	return visibleSlides;
};
// Move to the next set of slides based on visible slides count
const moveNextSlides = () => {
	const visibleSlides = getVisibleSlidesCount();
	const newIndex = (swiper?.activeIndex ?? 0) + visibleSlides;
	try {
		swiper?.slideTo?.(newIndex - 1);
	} catch (error) {
		console.error("Error moving to next slides", error);
	}
};
// Move to the previous set of slides based on visible slides count
const movePrevSlides = () => {
	const visibleSlides = getVisibleSlidesCount();
	const newIndex = (swiper?.activeIndex ?? 0) - visibleSlides;
	try {
		swiper?.slideTo?.(newIndex - 1);
	} catch (error) {
		console.error("Error moving to previous slides", error);
	}
};
// Calculate the height of the swiper container based on breakpoints and container width
function getSwiperHeight() {
	const aspectRatio = 236 / 176;
	const containerWidth = containerRef?.clientWidth || window.innerWidth;
	const breakpointsConfig = {
		200: 3.2,
		640: 4.2,
		720: 6,
		1280: 8
	};
	let slidesPerViewCount = 3.2;
	// Determine slidesPerView based on the container's width
	for (const [breakpoint, slides] of Object.entries(breakpointsConfig)) {
		if (containerWidth >= Number(breakpoint)) {
			slidesPerViewCount = slides;
		}
	}
	const spaceBetweenPx = spaceBetween || 8;
	const slideWidth = (containerWidth - spaceBetweenPx * (slidesPerViewCount - 1)) / slidesPerViewCount;
	return `${slideWidth * aspectRatio}px`;
}
// Method to center the today reward if the content type are rewards
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future rewards centering
const getTodayRewardIndex = () => {
	if (!data || !data.length) return 0;
	// eslint-disable-next-line svelte/prefer-svelte-reactivity -- date-only comparison helper
	const today = new Date().setHours(0, 0, 0, 0);
	const todayIndex = data.findIndex((reward) => {
		// eslint-disable-next-line svelte/prefer-svelte-reactivity -- date-only comparison helper
		return new Date(asRecord(reward).date).setHours(0, 0, 0, 0) === today;
	});
	return todayIndex !== -1 ? todayIndex : 0;
};
// Calculate visible slides count based on screen size (not container)
const calculateInitialVisibleSlides = () => {
	const screenWidth = window.innerWidth;
	// Mobile: 4 slides, Tablet: 6 slides, Desktop: 8 slides
	if (screenWidth >= 1280) {
		return 8;
	} else if (screenWidth >= 720) {
		return 6;
	}
	return 4;
};
// Initialize Swiper instance with custom configurations
const initializeSwiper = () => {
	if (!swiperContainer) return;
	// Destroy existing Swiper instance before initializing a new one
	if (swiper) {
		swiper.destroy(true, true);
	}
	// Configure breakpoints for responsive slides per view
	const breakpointsConfig = Object.keys(customSlidesPerView).length ? Object.entries(customSlidesPerView).reduce((acc, [breakpoint, slides]) => {
		acc[breakpoint] = { slidesPerView: slides };
		return acc;
	}, {}) : {
		200: { slidesPerView: slidesPerView || 3.2 },
		640: { slidesPerView: slidesPerView || 4.2 },
		720: { slidesPerView: slidesPerView || 6 },
		1280: { slidesPerView: slidesPerView || 8 }
	};
	// Initialize Swiper with navigation and autoplay modules
	swiper = new Swiper(swiperContainer, {
		modules: [
			Navigation,
			Autoplay,
			Mousewheel,
			FreeMode
		],
		loop: false,
		loopAddBlankSlides: false,
		navigation: {
			nextEl: `#${nextButtonId}`,
			prevEl: `#${prevButtonId}`
		},
		spaceBetween,
		slidesOffsetBefore: 0,
		mousewheel: { forceToAxis: true },
		freeMode: {
			enabled: true,
			momentum: true,
			momentumRatio: freeModeMomentumRatio,
			momentumVelocityRatio: freeModeMomentumVelocityRatio
		},
		touchRatio,
		grabCursor: true,
		on: { init: () => {
			document.getElementById(nextButtonId)?.addEventListener("click", moveNextSlides);
			document.getElementById(prevButtonId)?.addEventListener("click", movePrevSlides);
		} },
		breakpoints: breakpointsConfig
	});
	if (centerSlides) {
		const centerOffset = Math.floor(getSlidesPerView() / 2);
		const slideIndex = centerIndex ? Math.max(centerIndex - centerOffset, 0) : Math.max(Math.ceil(data.length / 2) - centerOffset, 0);
		// console.log(
		// 	`centerIndex: ${centerIndex} centerOffset: ${centerOffset} view ${getSlidesPerView()}`
		// );
		try {
			swiper?.slideTo?.(slideIndex, 0);
		} catch (error) {
			console.error("Error sliding to center index", error);
		}
	}
};
const gotoLink = () => {
	const link = toSiteUrl(url, $loggedIn) || "#";
	if (link !== "#") {
		// eslint-disable-next-line svelte/no-navigation-without-resolve -- dynamic site URL from toSiteUrl helper
		goto(link);
	}
};
// Subscribe to the locale store when the component is mounted
onMount(() => {
	mounted = true;
});
// Reactively update swiper height and visible slides count when the container ref is available
$effect(() => {
	if (containerRef) {
		swiperHeight = getSwiperHeight();
		visibleSlidesCount = calculateInitialVisibleSlides();
	}
});
// Initialize the Swiper instance when the swiper container is available and mounted
$effect(() => {
	if (!swiperContainer || !mounted || centerSlides && !centerIndexReady) {
		return;
	}
	initializeSwiper();
	// Attach touch handlers to prevent browser back gesture
	const container = swiperContainer;
	container.addEventListener("touchstart", handleTouchStart, { passive: true });
	container.addEventListener("touchmove", handleTouchMove, { passive: false });
	return () => {
		if (swiper) swiper.destroy(true, true);
		// Remove touch event listeners
		container.removeEventListener("touchstart", handleTouchStart);
		container.removeEventListener("touchmove", handleTouchMove);
	};
});
let sectionIconSize = $derived($stateUi.layout.sectionIconSize || 24);
let useAlternative4 = $derived($stateUi.cardSwiper.cardSwiperStyle === "alternative4");
let viewAllAsButton = $derived($stateUi.cardSwiper.viewAllAsButton ?? false);
let showNavigationArrows = $derived($stateUi.cardSwiper.showNavigationArrows ?? true);
let disableEdgeMask = $derived($stateUi.cardSwiper.disableEdgeMask ?? false);
let useRemoteIcons = $derived($stateUi.cardSwiper.useRemoteIcons ?? false);
</script>

{#if mounted}
	<!-- eslint-disable svelte/no-navigation-without-resolve -- dynamic site URLs via toSiteUrl -->
	<div class="card-swiper-section" style={customStyle ? customStyle : ''}>
		{#if title}
			<div
				class:header-container={$stateUi.layout.useReducedMarginsInHomePage === true}
				class:header-container-alternative={$stateUi.layout.useReducedMarginsInHomePage === false}
			>
				<div class="title-container">
					{#if icon && typeof icon === 'string'}
						<div class="title-icon">
							{#if $stateUi.layout.useRemoteIcons === true}
								<RemoteIcon {icon} size={sectionIconSize} />
							{:else}
								<img src={icon} alt={title} />
							{/if}
						</div>
					{/if}
					{#if icon && typeof icon === 'function'}
						{@const IconComponent = icon as Component}
						<div class="title-icon">
							<IconComponent />
						</div>
					{/if}
					<div class="title-desktop" class:semibold={callFromHomePage}>
						{#if !titleNotClickable}
							<a href={toSiteUrl(url, $loggedIn) || '#'} class="view-all-link">
								<Text type="subtitle" subtitleSize="large" subtitleWeight="semibold">{title}</Text>
							</a>
						{:else}
							<Text type="subtitle" subtitleSize="large" subtitleWeight="semibold">{title}</Text>
						{/if}
					</div>
					<div class="title-mobile" class:semibold={callFromHomePage}>
						{#if !titleNotClickable}
							<a href={toSiteUrl(url, $loggedIn) || '#'} class="view-all-link">
								<Text type="subtitle" subtitleSize="small" subtitleWeight="semibold">{title}</Text>
							</a>
						{:else}
							<Text type="subtitle" subtitleSize="small" subtitleWeight="semibold">{title}</Text>
						{/if}
					</div>
					{@render titleExtra?.()}
				</div>
				{#if !comingSoon}
					<div class="navigation-container">
						{#if !noViewAllLink && viewAllOnClick}
							<button onclick={viewAllOnClick} class="view-all-link"
								>{`${$t(Verbiage.viewAll)}`}</button
							>
						{:else if !noViewAllLink}
							{#if viewAllAsButton}
								<Button
									variant="secondary"
									className="link-navigation-alternative"
									size="small"
									text={`${$t(Verbiage.viewAll)}`}
									onClick={gotoLink}
								/>
							{:else}
								<a
									href={toSiteUrl(url, $loggedIn) || '#'}
									class="view-all-link"
									class:view-all-link-alternative-4={useAlternative4}>{`${$t(Verbiage.viewAll)}`}</a
								>
							{/if}
						{/if}
						{#if showNavigationArrows}
							{#if false}
								<div class="total-badge">
									<span class="total-badge-text">{data?.length}</span>
								</div>
							{/if}
							<div class="button-container">
								<div
									id={prevButtonId}
									class="swiper-button-prev"
									class:swiper-button-prev-alternative-4={useAlternative4}
								>
									{#if useRemoteIcons}
										<RemoteIcon icon="ArrowLeftIcon" size={22} />
									{:else if useAlternative4}
										<span>
											<LocalIcon icon="ArrowIcon" />
										</span>
									{/if}
								</div>
								<div
									id={nextButtonId}
									class="swiper-button-next"
									class:swiper-button-next-alternative-4={useAlternative4}
								>
									{#if useRemoteIcons}
										<RemoteIcon icon="ArrowRightIcon" size={22} />
									{:else if useAlternative4}
										<span>
											<LocalIcon icon="ArrowIcon" />
										</span>
									{/if}
								</div>
							</div>
						{/if}
					</div>
				{/if}
			</div>
		{/if}
		{#if comingSoon}
			<img
				src={`${CDN_BASE}/banner/coming-soon-banner.png`}
				alt={$t(Verbiage.icon_alt, 'alts')}
				class="coming-soon-banner"
			/>
			<img
				src={`${CDN_BASE}/banner_m/coming-soon-banner-mobile.png`}
				alt={$t(Verbiage.icon_alt, 'alts')}
				class="coming-soon-banner-mobile"
			/>
		{:else if data && data.length > 0}
			<div
				class="swiper card-swiper-container
				{irregular ? 'card-swiper-container-irregular' : ''}"
				class:disable-edge-mask={disableEdgeMask}
				bind:this={swiperContainer}
				style="padding: {padding}; {customStyleSwiperContainer}"
			>
				<div class="swiper-wrapper">
					{#each data as item, index (`${index}-${asRecord(item).key ?? `${asRecord(item).game ?? ''}-${asRecord(item).prov ?? ''}`}-${$loggedIn}`)}
						{#if CustomCard}
							<div class="swiper-slide">
								{#if noCardLink}
									<CustomCard {item} id={asRecord(item).id as string | undefined} />
								{:else}
									<a
										href={asRecord(item).page
											? toSiteUrl(`/${asRecord(item).page}/`, $loggedIn) || '#'
											: '#'}
										class="swiper-card-link"
									>
										<CustomCard {item} id={asRecord(item).id as string | undefined} />
									</a>
								{/if}
							</div>
						{:else if 'price' in item}
							<div class="swiper-slide">
								<PriceCard item={asPriceItem(item)} />
							</div>
						{:else if asRecord(item).game && asRecord(item).prov}
							{@const gameItem = asGameInfo(item)}
							{#if asRecord(item).notAvailable}
								<div id={toCamelCase(gameItem.game)} class="swiper-slide">
									<Card
										item={gameItem}
										eagerLoad={index < visibleSlidesCount}
										width={cardWidth}
										height={cardHeight}
									/>
								</div>
							{:else}
								<a
									id={toCamelCase(gameItem.game)}
									href={toSiteUrl(
										`${providerBasePath(gameItem.prov ?? '')}${toProviderSlug(gameItem.prov ?? '')}/${gameItem.prov?.toLowerCase() === 'sports' ? gameItem.game : toGameSlug(gameItem.game)}`,
										$loggedIn
									) || '#'}
									class="swiper-slide"
								>
									<Card
										item={gameItem}
										eagerLoad={index < visibleSlidesCount}
										width={cardWidth}
										height={cardHeight}
									/>
								</a>
							{/if}
						{:else}
							{@const gameItem = asGameInfo(item)}
							<a
								id={toCamelCase(gameItem.game)}
								href={toSiteUrl(`/provider/${asRecord(item).page}/`, $loggedIn) || '#'}
								class="swiper-slide"
							>
								<Card
									item={gameItem}
									eagerLoad={index < visibleSlidesCount}
									width={cardWidth}
									height={cardHeight}
								/>
							</a>
						{/if}
					{/each}
				</div>
			</div>
		{:else}
			<div class="swiper-placeholder" style="height: {swiperHeight};"></div>
		{/if}
	</div>
	<!-- eslint-enable svelte/no-navigation-without-resolve -->
{:else}
	<div class="container-ref" bind:this={containerRef}></div>
{/if}

<style>
	:global(.link-navigation-alternative) {
		border-width: 0px !important;
}

	.container-ref {
		width: 100%;
}

	.swiper-placeholder {
		width: 100%;
		background-color: transparent;
}

	.card-swiper-section {
		position: relative;
		display: flex;
		width: 100%;
		flex-direction: column;
		overflow: visible;
		font-family: var(--font-main);
}

	.card-swiper-container {
		position: relative;
		height: auto;
		width: 100%;
		overflow: hidden;
		padding-top: calc(0.25rem * 3);
		padding-bottom: calc(0.25rem * 6);
		overscroll-behavior-x: contain;
}

	.card-swiper-container-irregular {
		width: calc(100% + 14px);
		margin-left: -7px;
		padding-left: 7px;
		padding-right: 7px;
	}

	@media (max-width: 719px) {
		.card-swiper-container-irregular {
			padding-right: 0;
			width: calc(100% + 28px);
		}
	}

	.card-swiper-container .swiper-slide {
		display: flex;
		width: -moz-fit-content;
		width: fit-content;
		justify-content: center;
		overflow: visible;
		opacity: 1;
}

	.header-container {
		margin-bottom: 0px;
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.header-container-alternative {
		margin-bottom: 0px;
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	@media (min-width: 1024px) {

		.header-container-alternative {
				margin-bottom: 0.75rem;
		}
}

	.title-container {
		display: flex;
		align-items: center;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	/*.title-container img, */
	.title-icon {
		margin-right: 0.25rem;
		display: flex;
		height: 1.5rem;
		align-items: center;
		justify-content: center;
}
	@media (min-width: 1024px) {

		.title-icon {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container img, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container img, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container svg, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container svg, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	:global(/*.title-container > svg, */.title-icon) {
		margin-right: 0.25rem;
		height: 1.5rem;
}

	@media (min-width: 1024px) {

		:global(/*.title-container > svg, */.title-icon) {
				margin-right: 0.35rem;
		}
}

	.title {
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.title {
				font-size: 1.5rem;
				line-height: 2rem;
		}
}

	.title-desktop {
		display: none;
}

	@media (min-width: 1024px) {

		.title-desktop {
				display: block;
		}
}

	.title-mobile {
		display: block;
}

	@media (min-width: 1024px) {

		.title-mobile {
				display: none;
		}
}

	.navigation-container {
		display: flex;
		align-items: center;
		gap: calc(0.25rem * 2);
}

	.view-all-link {
		background-color: transparent;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.view-all-link {
				font-size: 14px;
		}
}

	.view-all-link:hover {
		cursor: pointer;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.view-all-link-alternative-4 {
		display: flex;
		height: 32px;
		align-items: center;
		border-radius: 0.5rem;
		background-color: var(--color-secondary-500);
		padding-left: 1rem;
		padding-right: 1rem;
}

	.button-container {
		display: flex;
		height: 100%;
		align-items: center;
}

	.total-badge {
		margin-left: 0.5rem;
		border-radius: 6px;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 800;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		background: var(--gradient-primary);
}

	:global(.button-container .swiper-button-prev, .button-container .swiper-button-next) {
		position: relative;
		z-index: 99;
		margin-left: 0.75rem;
		margin-top: 0px;
		height: 2rem;
		width: 2rem;
		transform: none;
		cursor: pointer;
		border-radius: 0.5rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		outline: 2px solid transparent;
		outline-offset: 2px;
		background: var(--gradient-alternative-2);
		position: static !important;
		margin-top: 0 !important;
		height: 32px !important;
		width: 32px !important;
}

	:global(
		.button-container .swiper-button-prev:hover,
		.button-container .swiper-button-next:hover
	) {
		border-top-width: 2px;
		border-style: none;
		background: var(--gradient-alternative-2);
}

	.swiper-button-prev-alternative-4,
	.swiper-button-next-alternative-4 {
		border-width: 0px;
		background-color: var(--color-secondary-500);
		background-image: unset;
}

	.swiper-button-prev-alternative-4 > span,
	.swiper-button-next-alternative-4 > span {
		display: inline-block;
		height: 1rem;
		width: 1rem;
}

	.swiper-button-prev-alternative-4 > span {
		--tw-rotate: 90deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.swiper-button-next-alternative-4 > span {
		--tw-rotate: -90deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.swiper-button-prev-alternative-4::after,
	.swiper-button-next-alternative-4::after {
		content: '';
	}

	:global(
		.button-container .swiper-button-prev::after,
		.button-container .swiper-button-next::after
	) {
		font-size: 8px !important;
		color: #c2cdff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 900;
	}

	@media (max-width: 1023px) {
		:global(.button-container .swiper-button-prev, .button-container .swiper-button-next) {
			position: static !important;
			transform: none;
			margin-left: 5px;
			margin-top: 0 !important;
		}
	}

	.coming-soon-banner {
		display: none;
}

	@media (min-width: 1024px) {

		.coming-soon-banner {
				display: block;
		}
}

	.coming-soon-banner-mobile {
		display: block;
}

	@media (min-width: 1024px) {

		.coming-soon-banner-mobile {
				display: none;
		}
}

	.card-swiper-container {
		position: relative;
		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0%,
			black 4%,
			black 92%,
			transparent 98%
		);
		mask-image: linear-gradient(to right, transparent 0%, black 4%, black 92%, transparent 98%);
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
		padding-top: calc(0.25rem * 3);
		padding-bottom: calc(0.25rem * 6);
	}

	@media (min-width: 1024px) {
		.card-swiper-container {
			-webkit-mask-image: linear-gradient(
				to right,
				transparent 0%,
				black 1.5%,
				black 98.5%,
				transparent 99.5%
			);
			mask-image: linear-gradient(
				to right,
				transparent 0%,
				black 1.5%,
				black 98.5%,
				transparent 99.5%
			);
			-webkit-mask-repeat: no-repeat;
			mask-repeat: no-repeat;
			padding-top: calc(0.25rem * 3);
			padding-bottom: calc(0.25rem * 6);
		}
	}

	.disable-edge-mask {
		mask-image: none;
		-webkit-mask-image: none;
	}

	.semibold {
		font-weight: 600;
}

	.swiper-card-link {
		display: block;
		height: 100%;
		width: 100%;
}
</style>
